import React, { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { connectSocket, getSlug, api } from "./lib";
import "./overlay.css";

const W = 1920, H = 1080;

const P = {
  period:    { x: 1037.9317, y: 867.94,   w: 56.4596,  h: 42.9317 },
  awayScore: { x: 1273.79,   y: 867.94,   w: 56.4596,  h: 42.9317 },
  downs:     { x: 1112.7,    y: 867.94,   w: 140.66,   h: 42.9317 },
  awayName:  { x: 1355.48,   y: 867.94,   w: 375.466,  h: 42.9317 },
  homeName:  { x: 185.817,   y: 867.94,   w: 375.466,  h: 42.9317 },
  homeScore: { x: 595.513,   y: 867.94,   w: 56.4596,  h: 42.9317 },
  clock:     { x: 774.847,   y: 867.94,   w: 101.694,  h: 42.9317 },

  tdHome:    { x: 130,       y: 864.94,   w: 450.089,  h: 56 },
  tdAway:    { x: 1343.79,   y: 864.94,   w: 450.089,  h: 56 },

  flag:      { x: 1099.7,    y: 863.296,  w: 168,      h: 58 },
  timeout:   { x: 1099.7,    y: 863.296,  w: 168,      h: 58 },

  possHome:  { x: 148.061,   y: 872.422,  w: 15.9454,  h: 17.4413 },
  possAway:  { x: 1760.8,    y: 872.422,  w: 15.9454,  h: 17.4413 },

  home_t1:   { x: 838,       y: 938.273,  w: 12,       h: 8 },
  home_t2:   { x: 857,       y: 938.273,  w: 12,       h: 8 },
  home_t3:   { x: 876,       y: 938.273,  w: 12,       h: 8 },
  away_t3:   { x: 1028.45,   y: 938.273,  w: 13,       h: 8 },
  away_t2:   { x: 1047,      y: 938.273,  w: 13,       h: 8 },
  away_t1:   { x: 1066,      y: 938.273,  w: 13,       h: 8 },
};

const box = ({x,y,w,h}, extra={}) => ({
  position:'absolute', left:`${x}px`, top:`${y}px`,
  width:`${w}px`, height:`${h}px`,
  display:'flex', alignItems:'center', justifyContent:'center',
  pointerEvents:'none', ...extra,
});

const mmss = (s) => {
  const t = Math.max(0, parseInt(s||0,10));
  const mm = String(Math.floor(t/60)).padStart(2,'0');
  const ss = String(t%60).padStart(2,'0');
  return `${mm}:${ss}`;
};

function useGame(slug){
  const [g,setG] = useState(null);
  useEffect(()=>{
    let live = true;
    api(`/api/games/${slug}`).then(d=>{ if(live) setG(d); }).catch(()=>{});
    const s = connectSocket({ slug });
    s.on('state', setG);
    return ()=>{ live=false; s.disconnect(); };
  },[slug]);
  return g;
}

function Overlay(){
  const slug = getSlug();
  const g = useGame(slug);
  if(!g) return null;

  const home = g.home||{}, away=g.away||{}, clock=g.clock||{};
  const remain = side => Math.max(0, (side.timeoutsAllotted||3)-(side.timeoutsUsed||0));

  // Downs text logic with font sizes
  const { downsText, downsSize } = (() => {
    const d = g.downs || "";
    const y = g.yardsToGo;

    if (y == null || y === "N/A") {
      return { downsText: d ? `${d} Down` : "", downsSize: 25 };
    }
    if (y === 0) {
      return { downsText: d ? `${d} & GOAL` : "GOAL", downsSize: 22 };
    }
    return { downsText: d ? `${d} & ${y}` : `& ${y}`, downsSize: 28 };
  })();

  const tdChip = {
    background:'#ffeb3b', color:'#000',
    borderRadius:'4px', padding:'0 8px', lineHeight:'1.1',
    position:'relative', top:'4px'
  };

  return (
    <div style={{position:'fixed', inset:0, background:'#00ff00'}}>
      <div style={{position:'absolute', left:'50%', top:'50%', width:`${W}px`, height:`${H}px`, transform:'translate(-50%,-50%)'}}>
        <img src="/overlay/bg_plate.svg" alt="" style={{position:'absolute', left:0, top:0, width:'100%', height:'100%'}}/>

        {/* Names with yellow chip when TOUCHDOWN */}
        <div style={{...box(P.homeName), fontSize:'40px', color:'#fff', fontWeight:700, justifyContent:'flex-start', paddingLeft:'8px'}}>
          <span style={home.name === 'TOUCHDOWN' ? tdChip : undefined}>{home.name || 'Home'}</span>
        </div>
        <div style={{...box(P.awayName), fontSize:'40px', color:'#fff', fontWeight:700, justifyContent:'flex-end', paddingRight:'8px'}}>
          <span style={away.name === 'TOUCHDOWN' ? tdChip : undefined}>{away.name || 'Away'}</span>
        </div>

        {/* Scores / period / downs / clock */}
        <div style={{...box(P.homeScore), fontSize:'40px', color:'#fff', fontWeight:700}}>{home.score ?? 0}</div>
        <div style={{...box(P.awayScore), fontSize:'40px', color:'#fff', fontWeight:700}}>{away.score ?? 0}</div>
        <div style={{...box(P.period),    fontSize:'40px', color:'#fff', fontWeight:700}}>{g.period || 'Q1'}</div>
        <div style={{...box(P.downs), fontSize:`${downsSize}px`, color:'#fff', fontWeight:700}}>
          {downsText}
        </div>
        <div style={{...box(P.clock),     fontSize:'40px', color:'#fff', fontWeight:700}}>{mmss(clock.seconds||0)}</div>

        {/* Possession icons */}
        {g.possession==='home' && <img src="/overlay/possession_home.svg" style={box(P.possHome)} alt="home possession" />}
        {g.possession==='away' && <img src="/overlay/possession_away.svg" style={box(P.possAway)} alt="away possession" />}

        {/* Timeout pips */}
        {[3,2,1].slice(0, remain(home)).map(n => <div key={'ht'+n} style={{...box(P['home_t'+n]), background:'#ffeb3b', borderRadius:'2px'}} />)}
        {[3,2,1].slice(0, remain(away)).map(n => <div key={'at'+n} style={{...box(P['away_t'+n]), background:'#ffeb3b', borderRadius:'2px'}} />)}

        {/* Flag/Timeout banners stay while active */}
        {g.banners?.flag    && <img src="/overlay/banner_flag.svg"    className="pop" style={box(P.flag)} alt="flag" />}
        {g.banners?.timeout && <img src="/overlay/banner_timeout.svg" className="pop" style={box(P.timeout)} alt="timeout" />}
      </div>
    </div>
  );
}

createRoot(document.getElementById("root")).render(<Overlay/>);
